import { CurrencyPipe, DecimalPipe, PercentPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { PlatformService } from 'app/core/platform/platform.service';
import { PlatformSettings } from 'app/core/platform/platform.types';
import { AnalyticsService } from 'app/modules/user/analytics/analytics.service';
import { AnalyticsDataDTO, AnalyticsDataRow, AnalyticsRowMetadata, NewFilterConfig, SortConfig } from 'app/modules/user/analytics/analytics.types';
import { UtilService } from 'app/shared/util.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-top-list',
    templateUrl: './top-list.component.html',
    styleUrls: ['./top-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopListComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() colors: string[];
    @Input() data$: Observable<AnalyticsDataDTO>;
    @Input() currency?: string;
    @Input() comparisonMode?: 'artificials' | 'default';
    @Input() artificialsInclusive?: boolean = false;
    @Input() showPPS?: boolean = false;
    @Input() showMoreButton?: boolean = false;
    @Input() showTotalsRow?: boolean = false;
    @Input() enableSort?: boolean = false;
    @Input() disableFilterOnClick?: boolean = false;
    @Input() onlyStreams?: boolean = false;
    @Input() socials?: boolean = false;
    @Input() storeName?: string;
    @Output() onMoreButtonPressed = new EventEmitter<any>();
    @Output() onFilterAdded = new EventEmitter<NewFilterConfig>();
    @Output() onSortChanged = new EventEmitter<SortConfig>();

    data: AnalyticsDataRow[];
    artificialsInclusiveCache: boolean = false;

    totalRevenue: number;
    totalStreams: number;
    platform: PlatformSettings;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    @ViewChild('sort') private _sort: MatSort;

    constructor(
        private analyticsService: AnalyticsService,
        private _changeDetectorRef: ChangeDetectorRef,
        public utilService: UtilService,
        public platformService: PlatformService,
        public percentPipe: PercentPipe,
        public currencyPipe: CurrencyPipe,
        public decimalPipe: DecimalPipe,
    ) {
    }

    ngOnInit(): void {
        this.platformService.platform$.pipe(takeUntil(this._unsubscribeAll)).subscribe(platform => this.platform = platform);
        this.data$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                this.totalRevenue = response?.data?.reduce((prev, cur) => prev + cur.totals.revenue, 0) ?? 0;
                if (this.totalRevenue <= 0) {
                    this.totalRevenue = 1;
                }

                if (response.data.length !== 1) {
                    this.totalStreams = response.data.filter(d => d.metadata.title !== "TikTok").reduce((prev, cur) => prev + cur.totals.streams, 0);
                } else {
                    this.totalStreams = response.data.reduce((prev, cur) => prev + cur.totals.streams, 0);
                }

                this.data = response.data;
                this.artificialsInclusiveCache = this.artificialsInclusive;
                this._changeDetectorRef.markForCheck();
            })
    }

    ngAfterViewInit(): void {
        if (!this.enableSort) {
            return;
        }

        this._sort?.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sortConfig) => {
            this.onSortChanged.emit({ sortBy: sortConfig.active, sortDirection: sortConfig.direction });
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    moreRows() {
        this.onMoreButtonPressed.emit();
    }

    addFilter(metadata: AnalyticsRowMetadata) {
        this.onFilterAdded.next({
            type: undefined, //not available here. will be set in parent component
            value: { uuid: metadata.uuid, title: metadata.title, logo: metadata.logo ?? metadata.artwork ?? metadata.avatar }
        });
    }

    openVideo(videoId: string) {
        window.open('https://www.youtube.com/watch?v=' + videoId, '_blank');
    }

    getTotalsRow(): AnalyticsDataRow {
        let totals: AnalyticsDataRow = {
            metadata: {
                title: "Total",
                uuid: "total",
                logo: undefined,
                artwork: undefined,
                avatar: undefined,
            },
            totals: {
                revenue: this.totalRevenue,
                downloads: this.data.reduce((prev, cur) => prev + cur.totals.downloads, 0),
                streams: this.totalStreams,
                creations: this.data.reduce((prev, cur) => prev + cur.totals.creations, 0),
                likes: this.data.reduce((prev, cur) => prev + cur.totals.likes, 0),
                comments: this.data.reduce((prev, cur) => prev + cur.totals.comments, 0),
                shares: this.data.reduce((prev, cur) => prev + cur.totals.shares, 0),
                favorites: this.data.reduce((prev, cur) => prev + cur.totals.favorites, 0),
                avg_duration: this.data.reduce((prev, cur, i) => prev + (cur.totals.avg_duration * this.data[i].totals.streams), 0) / this.totalStreams,
                pps: 0,
            },
            comparison: {
                creations: this.data.reduce((prev, cur) => prev + (cur.comparison?.creations ?? 0), 0),
                downloads: this.data.reduce((prev, cur) => prev + (cur.comparison?.downloads ?? 0), 0),
                streams: this.data.reduce((prev, cur) => prev + (cur.comparison?.streams ?? 0), 0),
                revenue: this.data.reduce((prev, cur) => prev + (cur.comparison?.revenue ?? 0), 0),
                pps: null,
            }
        }

        if (this.data.find(d => d.metadata?.uuid === "others") || !this.data.find(d => !!d.comparison)) {
            totals.comparison = undefined;
        }

        if (this.showPPS) {
            totals.totals.pps = totals.totals.revenue / totals.totals.streams;
        }

        return totals;
    }


    getPercentageString(value: number, totalValue: number): string {
        let percentage = 0;
        if (totalValue <= 0) {
            return this.percentPipe.transform(0, '1.0-0');
        }
        percentage = value / totalValue;
        let percentageString = this.percentPipe.transform(value / totalValue, '1.0-0');
        if (percentageString === "0%" && percentage > 0) {
            percentageString = '<1%';
        } else if (percentageString === "100%" && percentage < 0.999) {
            percentageString = ">99%";
        }
        return percentageString;
    }

    getComparisonPercentageString(data: number, comparison: number): string {
        let percentage = 0;
        if (comparison <= 0) {
            return this.percentPipe.transform(0, '1.0-0');
        } else {
            //calculate change in percentage when going from data (before) to comparison (after)
            percentage = (data - comparison) / comparison;
        }
        let percentageString = this.percentPipe.transform(percentage, '1.0-0');
        if (percentageString === "0%" && percentage > 0) {
            percentageString = '<1%';
        } else if (percentageString === "100%" && percentage < 0.999) {
            percentageString = ">99%";
        }
        return percentageString;
    }

    getArtificialsComparisonPercentageString(data: number, comparison: number): string {
        let percentage = 0;
        if (comparison <= 0) {
            return this.percentPipe.transform(0, '1.0-0');
        } else {
            //calculate change in percentage when going from data (before) to comparison (after)
            if (this.artificialsInclusiveCache) {
                percentage = data / comparison;
            } else {
                percentage = data / (comparison + data);
            }
        }
        let percentageString = this.percentPipe.transform(percentage, '1.0-1');
        if (percentageString === "0%" && percentage > 0) {
            percentageString = '<1%';
        } else if (percentageString === "100%" && percentage < 0.999) {
            percentageString = ">99%";
        }
        return percentageString;
    }

    formatComparisonTooltip(value: number, currency?: string): string {
        if (currency) {
            return this.currencyPipe.transform(value, currency, 'symbol', '1.2-2');
        } else {
            return this.decimalPipe.transform(value, '1.0-0');
        }
    }

    getColorForIndex(index: number): string {
        if (this.colors.length === 0 || index > 5) {
            return '#95a5a6';
        }

        if (0 <= index && index < this.colors.length) {
            return this.colors[index];
        }
        return this.colors[this.colors.length - 1];
    }
}
